import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../App";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Users, Trophy, Target, TrendingUp } from "lucide-react";
import { Progress } from "@/components/ui/progress";

const ParentDashboard = () => {
  const { token, user } = useContext(AuthContext);
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);
  const [childProgress, setChildProgress] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.role !== "parent") {
      toast.error("Access denied: Parent only");
      return;
    }
    fetchChildren();
  }, []);

  const fetchChildren = async () => {
    try {
      const response = await axios.get(`${API}/parent/children`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setChildren(response.data);
      if (response.data.length > 0) {
        fetchChildProgress(response.data[0].id);
      }
    } catch (error) {
      toast.error("Failed to load children");
    } finally {
      setLoading(false);
    }
  };

  const fetchChildProgress = async (childId) => {
    try {
      const response = await axios.get(`${API}/parent/children/${childId}/progress`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setChildProgress(response.data);
      setSelectedChild(childId);
    } catch (error) {
      toast.error("Failed to load child progress");
    }
  };

  if (user?.role !== "parent") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <Card>
            <CardContent className="p-12 text-center">
              <h2 className="text-2xl font-bold text-red-600 mb-2">Access Denied</h2>
              <p className="text-gray-600">You don't have permission to view this page.</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
        </div>
      </div>
    );
  }

  if (children.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <Card>
            <CardContent className="p-12 text-center">
              <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h2 className="text-2xl font-bold mb-2">No Children Linked</h2>
              <p className="text-gray-600 mb-4">You haven't linked any children to your parent account yet.</p>
              <p className="text-sm text-gray-500">Ask your child to register with your email as parent email.</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 py-8" data-testid="parent-dashboard">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2 flex items-center gap-3">
            <Users className="w-10 h-10 text-indigo-500" />
            Parent Dashboard
          </h1>
          <p className="text-gray-600 text-lg">Monitor your child's progress and achievements</p>
        </div>

        {/* Children Selection */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Your Children</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {children.map((child) => (
                <Card
                  key={child.id}
                  className={`cursor-pointer card-hover ${selectedChild === child.id ? 'ring-2 ring-indigo-500' : ''}`}
                  onClick={() => fetchChildProgress(child.id)}
                >
                  <CardContent className="p-6 text-center">
                    <Avatar className="w-16 h-16 mx-auto mb-3 bg-gradient-to-br from-indigo-500 to-purple-600">
                      <AvatarFallback className="text-white text-xl font-bold">
                        {child.name.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <h3 className="font-semibold text-lg">{child.name}</h3>
                    <p className="text-sm text-gray-600">Grade {child.grade}</p>
                    <Badge className="mt-2">{child.points || 0} points</Badge>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Child Progress */}
        {childProgress && (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">Total Points</CardTitle>
                  <Trophy className="w-5 h-5 text-yellow-500" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{childProgress.child.points}</div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">Assessments</CardTitle>
                  <Target className="w-5 h-5 text-blue-500" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{childProgress.assessments.length}</div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">Progress</CardTitle>
                  <TrendingUp className="w-5 h-5 text-green-500" />
                </CardHeader>
                <CardContent>
                  <Progress value={childProgress.assessments.length * 20} className="mb-2" />
                  <p className="text-sm text-gray-600">On track</p>
                </CardContent>
              </Card>
            </div>

            {/* Recent Assessments */}
            <Card>
              <CardHeader>
                <CardTitle>Recent Assessment Results</CardTitle>
                <CardDescription>Latest career assessment results and recommendations</CardDescription>
              </CardHeader>
              <CardContent>
                {childProgress.assessments.length > 0 ? (
                  <div className="space-y-4">
                    {childProgress.assessments.slice(0, 3).map((assessment, idx) => (
                      <Card key={idx} className="bg-gray-50">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <p className="font-semibold">{assessment.assessment_id}</p>
                              <p className="text-sm text-gray-600">
                                {new Date(assessment.created_at).toLocaleDateString()}
                              </p>
                            </div>
                            <Badge>+50 points</Badge>
                          </div>
                          <div className="mb-3">
                            <p className="text-sm font-semibold mb-2">Strengths:</p>
                            <div className="flex flex-wrap gap-2">
                              {Object.entries(assessment.score).map(([key, value]) => (
                                <Badge key={key} variant="secondary">
                                  {key}: {value}
                                </Badge>
                              ))}
                            </div>
                          </div>
                          <div>
                            <p className="text-sm font-semibold mb-2">Recommendations:</p>
                            <ul className="space-y-1">
                              {assessment.recommendations.slice(0, 2).map((rec, ridx) => (
                                <li key={ridx} className="text-sm text-gray-600 flex items-start gap-2">
                                  <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full mt-1.5"></span>
                                  <span>{rec}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Target className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No assessments completed yet.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default ParentDashboard;